import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Filter, X } from "lucide-react";

const STORAGE_PREFIX = "sf_filters_";

function useSessionFilters(pageKey, defaults) {
  const [filters, setFilters] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_PREFIX + pageKey);
      return saved ? { ...defaults, ...JSON.parse(saved) } : defaults;
    } catch { return defaults; }
  });
  useEffect(() => {
    sessionStorage.setItem(STORAGE_PREFIX + pageKey, JSON.stringify(filters));
  }, [filters, pageKey]);
  return [filters, setFilters];
}

function countActive(filters, defaults) {
  let count = 0;
  for (const key in filters) {
    if (JSON.stringify(filters[key]) !== JSON.stringify(defaults[key])) count++;
  }
  return count;
}

function FilterDropdown({ label, value, onChange, options, testId }) {
  return (
    <div className="min-w-[140px]">
      <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1 block">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className="bg-zinc-50 border-zinc-300 rounded-sm text-xs h-8">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function DateInput({ label, value, onChange, testId }) {
  return (
    <div className="min-w-[130px]">
      <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1 block">{label}</label>
      <Input
        data-testid={testId}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-zinc-50 border-zinc-300 rounded-sm text-xs h-8"
      />
    </div>
  );
}

function AmountInput({ label, value, onChange, placeholder, testId }) {
  return (
    <div className="min-w-[100px]">
      <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1 block">{label}</label>
      <Input
        data-testid={testId}
        type="number"
        step="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-zinc-50 border-zinc-300 rounded-sm text-xs h-8"
      />
    </div>
  );
}

function FilterContent({ config, filters, setFilter }) {
  return (
    <div className="flex flex-wrap gap-3 items-end">
      {config.map((f) => {
        if (f.type === "select") {
          return (
            <FilterDropdown
              key={f.key}
              label={f.label}
              value={filters[f.key]}
              onChange={(v) => setFilter(f.key, v)}
              options={f.options}
              testId={`filter-${f.key}`}
            />
          );
        }
        if (f.type === "date") {
          return (
            <DateInput
              key={f.key}
              label={f.label}
              value={filters[f.key]}
              onChange={(v) => setFilter(f.key, v)}
              testId={`filter-${f.key}`}
            />
          );
        }
        if (f.type === "amount") {
          return (
            <AmountInput
              key={f.key}
              label={f.label}
              value={filters[f.key]}
              onChange={(v) => setFilter(f.key, v)}
              placeholder={f.placeholder}
              testId={`filter-${f.key}`}
            />
          );
        }
        return null;
      })}
    </div>
  );
}

export default function FilterBar({ pageKey, defaults, config, filters, setFilters, filteredCount, totalCount }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const activeCount = countActive(filters, defaults);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearAll = () => setFilters({ ...defaults });

  return (
    <>
      {/* Desktop filters */}
      <div className="hidden md:block border border-zinc-200 bg-white p-3 rounded-sm mb-4" data-testid="filter-bar">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Filter className="w-3.5 h-3.5 text-zinc-400" />
            <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Filters</span>
            {activeCount > 0 && (
              <Badge variant="secondary" className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0" data-testid="filter-count-badge">
                {activeCount}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-zinc-400 font-mono" data-testid="filter-result-count">
              Showing {filteredCount} of {totalCount}
            </span>
            {activeCount > 0 && (
              <Button data-testid="clear-all-filters" variant="ghost" size="sm" onClick={clearAll} className="text-xs text-red-500 hover:text-red-600 h-6 px-2">
                <X className="w-3 h-3 mr-1" /> Clear All
              </Button>
            )}
          </div>
        </div>
        <FilterContent config={config} filters={filters} setFilter={setFilter} />
      </div>

      {/* Mobile filter button */}
      <div className="md:hidden flex items-center gap-2 mb-4">
        <Button
          data-testid="mobile-filter-button"
          variant="outline"
          size="sm"
          onClick={() => setMobileOpen(true)}
          className="rounded-sm text-xs border-zinc-300"
        >
          <Filter className="w-3 h-3 mr-1" />
          Filters
          {activeCount > 0 && (
            <Badge variant="secondary" className="text-[10px] bg-orange-100 text-orange-700 ml-1 px-1.5 py-0">
              {activeCount}
            </Badge>
          )}
        </Button>
        <span className="text-xs text-zinc-400 font-mono" data-testid="mobile-filter-result-count">
          {filteredCount} of {totalCount}
        </span>
        {activeCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearAll} className="text-xs text-red-500 h-6 px-2">
            <X className="w-3 h-3" />
          </Button>
        )}
      </div>

      {/* Mobile sheet */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="bottom" className="rounded-t-xl max-h-[80vh] overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle className="text-sm font-bold">
              Filters
              {activeCount > 0 && (
                <Badge variant="secondary" className="text-[10px] bg-orange-100 text-orange-700 ml-2">{activeCount}</Badge>
              )}
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-3">
            {config.map((f) => {
              if (f.type === "select") {
                return (
                  <FilterDropdown key={f.key} label={f.label} value={filters[f.key]} onChange={(v) => setFilter(f.key, v)} options={f.options} testId={`mobile-filter-${f.key}`} />
                );
              }
              if (f.type === "date") {
                return (
                  <DateInput key={f.key} label={f.label} value={filters[f.key]} onChange={(v) => setFilter(f.key, v)} testId={`mobile-filter-${f.key}`} />
                );
              }
              if (f.type === "amount") {
                return (
                  <AmountInput key={f.key} label={f.label} value={filters[f.key]} onChange={(v) => setFilter(f.key, v)} placeholder={f.placeholder} testId={`mobile-filter-${f.key}`} />
                );
              }
              return null;
            })}
          </div>
          <div className="flex gap-2 mt-4 pt-3 border-t border-zinc-200">
            {activeCount > 0 && (
              <Button variant="outline" size="sm" onClick={clearAll} className="rounded-sm text-xs flex-1">Clear All</Button>
            )}
            <Button size="sm" onClick={() => setMobileOpen(false)} className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm text-xs flex-1">
              Show {filteredCount} Results
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

export { useSessionFilters };
